'use client';
'use client';

import { useState, useCallback, useRef, useEffect } from 'react';

interface UseButtonProtectionOptions {
  timeout?: number;
  onTimeout?: () => void;
}

interface UseButtonProtectionReturn {
  isProtected: boolean;
  protect: () => void;
  unprotect: () => void;
  executeWithProtection: <T>(fn: () => Promise<T>) => Promise<T>;
}

export function useButtonProtection(
  options: UseButtonProtectionOptions = {}
): UseButtonProtectionReturn {
  const { timeout = 10000, onTimeout } = options;
  const [isProtected, setIsProtected] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const protect = useCallback(() => {
    setIsProtected(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setIsProtected(false);
      if (onTimeout) {
        onTimeout();
      }
    }, timeout);
  }, [timeout, onTimeout]);

  const unprotect = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsProtected(false);
  }, []);

  const executeWithProtection = useCallback(
    async <T>(fn: () => Promise<T>): Promise<T> => {
      if (isProtected) {
        throw new Error('Action already in progress');
      }

      protect();
      try {
        const result = await fn();
        unprotect();
        return result;
      } catch (error) {
        unprotect();
        throw error;
      }
    },
    [isProtected, protect, unprotect]
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    isProtected,
    protect,
    unprotect,
    executeWithProtection,
  };
}
